import { ElementRef, OnDestroy, AfterViewInit, Component, Input, OnChanges, ViewChild, SimpleChanges, OnInit } from '@angular/core';
import { Subscription, combineLatest, of, startWith } from 'rxjs';
import { map } from 'rxjs/operators';

import { VbBaseMenuComponent } from './shared/VbBaseMenu.Component';

import { VbPlaybackManagerComponent } from './VbPlaybackManager.Component';
import { VideogularComponent } from './videogular/Videogular.Component';
import { VbPlaybackRateButtonComponent } from './VbPlaybackRateButton.Component';
import { IPlayerSettingsMenu, MENU_ITEM_ID } from './IPlayerSettingsMenu';
import { IVbSource } from './IVbSource';
import { SettingsKey, formatHotkeyHelp } from './hotkeys/VbHotKeys.Contract';

@Component({
	selector: 'vb-player-settings-menu',
	host: {
		class: 'menuButtonContainer rootMenuButton'
	},
	template: `
		<div dropdown
			#dropdown="bs-dropdown"
			placement="top right"
			[dropup]="true">

			<button
				class="menuButton glyphicons cogwheel"
				type="button"
				dropdownToggle
				vbControlBarFocus
				id="buttonSettingsMenu"
				[attr.aria-label]="buttonLabel"
				[tooltip]="buttonLabel"
				(click)="onSettingClick()">
			</button>

			<ul
				class="dropdown-menu dropdownMenu playerSettingsMenu"
				*dropdownMenu
				#vbDropdownMenu
				id="dropdownSettingsmenu"
				role="menu"
				aria-labelledby="buttonSettingsMenu"
				tabindex="0">
					<li class="menuHeading"><span>{{translations.playerSettingsMenu}}</span></li>
				@for (menuItem of menuItems; track menuItem.id) {
					<li role="none">
						<button
							type="button"
							role="menuitem"
							class="dropdown-item btn-link menuItem"
							(click)="onMenuItemClick(menuItem)"
							[attr.id]="'buttonMenu'+ menuItem.id"
							[ngClass]="menuItem.hasSubmenu ? 'hasSubmenu' : ''">
							<span class="menuItemLabel">{{ menuItem.label }}</span>

							<span class="menuItemContent">{{ menuItem.content }}</span>
						</button>
					</li>
				}
			</ul>
		</div>
		@if (!disablePlaybackRate) {
			<vb-playback-rate-button
				#vbPlaybackRateComp
				[hidden]="true"
				[parentElement]="elementRef"
				[activeSource]="vgApi.activeSource"
				[activateDropdown]="activeMenuId === MenuItemIds.PlaybackRate"
				[translations]="translations"
				(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
			</vb-playback-rate-button>
		}
		<vb-playback-menu-button
			[hidden]="true"
			[parentElement]="elementRef"
			[playbackManager]="playbackManager"
			[translations]="translations"
			[activateDropdown]="activeMenuId === MenuItemIds.PlaybackSource"
			(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
		</vb-playback-menu-button>
		@if (!disableHotkeys) {
			<vb-hotkeys-help
				[hidden]="true"
				[parentElement]="elementRef"
				[translations]="translations"
				[activateDropdown]="activeMenuId === MenuItemIds.Hotkeys"
				(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
			</vb-hotkeys-help>
		}
	`,
	standalone: false,
	styleUrls: ['./VbPlayerSettingsMenu.Component.ng.less', './MenuButton.ng.less']
})
export class VbPlayerSettingsMenuComponent extends VbBaseMenuComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
	@Input() public isLive: boolean;
	@Input() public disableHotkeys: boolean;
	@Input() public disablePlaybackRate: boolean;
	@Input() public translations: any;
	@Input() public playbackManager: VbPlaybackManagerComponent;
	@Input() public activeSource: any;
	@Input() public playbackOptions: IVbSource[];

	@ViewChild('vbPlaybackRateComp')
	public vbPlaybackRateComp: VbPlaybackRateButtonComponent;

	public menuItems: IPlayerSettingsMenu[];
	public activeMenuId: MENU_ITEM_ID = MENU_ITEM_ID.None;

	public readonly MenuItemIds = MENU_ITEM_ID;

	private context: any = {};
	private subscription: Subscription;
	public buttonLabel: string;

	constructor(
		public elementRef: ElementRef,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.isLive) {
			this.shapeMenuItems();
		}
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.buttonLabel = formatHotkeyHelp(this.translations.playerSettingsMenu, SettingsKey);
	}

	public ngAfterViewInit(): void {
		this.subscription = combineLatest([
			this.vbPlaybackRateComp?.playbackRateEnabled$ ?? of(false),
			this.vgApi.vgActiveSourceChange.pipe(startWith(null)),
			this.playbackManager.context$
		]).pipe(
			map(([playbackRateEnabled, activeSource, playbackManagerContext]) => {
				return {
					playbackRateEnabled: playbackRateEnabled ?? false,
					activeSource: activeSource?.$source ?? {},
					supportedPlaybackOptions: playbackManagerContext?.supportedPlaybackOptions
				};
			})
		).subscribe(context => {
			this.context = context;
			this.shapeMenuItems();
		});
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.subscription?.unsubscribe();
	}

	public onMenuItemClick(menu: IPlayerSettingsMenu): void {
		if (!menu.hasSubmenu) {
			return;
		}
		this.dropdown.hide();
		this.activeMenuId = menu.id;
	}

	private shapeMenuItems(): void {
		this.menuItems = [
			!this.isLive && this.context.playbackRateEnabled && {
				id: MENU_ITEM_ID.PlaybackRate,
				label: this.translations.playbackSpeed,
				content: this.vgApi.playback === 1 ? this.translations.normal : this.vgApi.playback + 'x',
				hasSubmenu: true
			},
			this.context.activeSource && {
				id: MENU_ITEM_ID.PlaybackSource,
				label: this.translations.playbackSource,
				content: this.context.activeSource?.label,
				hasSubmenu: this.context.supportedPlaybackOptions?.length > 1
			},
			this.context.activeSource && !this.disableHotkeys && {
				id: MENU_ITEM_ID.Hotkeys,
				label: this.translations.keyboardShortcuts,
				hasSubmenu: true
			}
		].filter(Boolean);
	}

	public resurfacePrimaryMenu(closeParent?: boolean): void {
		this.activeMenuId = MENU_ITEM_ID.None;

		if (closeParent) {
			return;
		}
		setTimeout(() => {
			this.shapeMenuItems();
			this.dropdown.show();
		}, 100);
	}

	public onSettingClick(): void {
		this.activeMenuId = MENU_ITEM_ID.None;
	}
	public toggleDropdown(): void {
		this.onSettingClick();
		super.toggleDropdown();
	}
}
